import {get} from 'svelte/store';
import {PATHS} from '@/const/paths';
import {loginUtil} from '@/helper/login';
import {abs, gt, lt, numUtil, pow10, times, toFixed} from '@/helper/num';
import {popupUtil} from '@/helper/popup';
import {fixLink$} from '@/store/lang';
import {isLogin$} from '@/streams/auth';
import {innerWidth$} from '@/streams/window-inner-size';
import {userInfo$} from '@/streams/user';
import {isActivateLendingAccount$} from '@/streams/lending/state';
import {kycSummary$} from '@/modules/account/overview/stream-kyc';
import IdentityVerification from '../popups/identity-verification/IdentityVerification.svelte';
import ActivityLendingAccount from '../popups/activity-lending-account/ActivityLendingAccount.svelte';

export function updateLendingState(userStatus, isSupportLending) {
  if (userStatus === 'Init' || isSupportLending) {
    return;
  }
  const fixLink = get(fixLink$);
  window.location.replace(fixLink(PATHS.HOME.pathname));
}

export function checkLogin(LG) {
  const isLogin = isLogin$.getValue();
  if (isLogin) {
    return true;
  }
  const innerWidth = innerWidth$.getValue();
  const fixLink = get(fixLink$);
  loginUtil.loginOrRegister(innerWidth, fixLink, LG, location.href);
  return false;
}

export function checkKyc(content) {
  const userInfo = userInfo$.getValue();
  const kycSummary = kycSummary$.getValue();
  if (userInfo.isMain && kycSummary.advanceKycStatus !== 'Verified' && kycSummary.basicKycStatus !== 'Verified') {
    popupUtil.create(IdentityVerification, content);
    return false;
  }
  return true;
}

export function checkActivityLendingAccount() {
  const isActivateLendingAccount = isActivateLendingAccount$.getValue();
  if (!isActivateLendingAccount) {
    popupUtil.create(ActivityLendingAccount);
    return false;
  }
  return true;
}

export function convertDisplayValue(value: string | number, precision: number = 8, isTrimEnd0 = true, rm = 0) {
  if (!value && value !== 0) {
    return '--';
  }
  if (isTrimEnd0) {
    return numUtil.delimit(numUtil.trimEnd0(toFixed(value, precision, rm)));
  }
  return numUtil.delimit(toFixed(value, precision, rm));
}

export function convertDisplayValueLtMin(value: string | number, precision: number = 8, isTrimEnd0 = true, rm = 0) {
  if (!value && value !== 0) {
    return '--';
  }
  const min = pow10(-precision, true);
  if (gt(abs(value), 0) && gt(min, abs(value))) {
    const valStr = '< ' + pow10(-precision, true);
    return lt(value, 0) ? `- (${valStr})` : valStr;
  }
  return convertDisplayValue(value, precision, isTrimEnd0, rm);
}

export function convertDisplayPercentage(value: string | number, precision: number = 4, isTrimEnd0 = true, rm = 0) {
  if (!value && value !== 0) {
    return '--';
  }

  let value2 = toFixed(times(value, 100), precision, rm);
  if (isTrimEnd0) {
    value2 = numUtil.trimEnd0(value2);
  }
  return value2 + '%';
}

export function convertDisplayPercentageLtMin(value: string | number, precision: number = 4, isTrimEnd0 = true, rm = 0) {
  if (!value && value !== 0) {
    return '--';
  }

  const min = pow10(-(precision + 2), true);
  if (gt(value, 0) && gt(min, value)) {
    return '< ' + pow10(-precision, true) + '%';
  }

  return convertDisplayPercentage(value, precision, isTrimEnd0, rm);
}

export function reconvertDelimitValue(value: string, precision: number = 8, isTrimEnd0 = true, rm = 0) {
  const value2 = value.replace(',', '');

  if (!value2 || Number.isNaN(+value2)) {
    return value;
  }
  return convertDisplayValue(value.replace(',', ''), precision, isTrimEnd0, rm);
}

export function createDefaultCurrencyInfo(currency = '', info = {}): CurrencyEntity {
  return {
    currency,
    maxValueEv: 0,
    minValueEv: 0,
    name: currency,
    needAddrTag: 0,
    valueFactor: 1,
    valuePrecision: 8,
    valueScale: 0,
    displayCurrency: currency,
    inAssetsDisplay: 0,
    qtyPrecision: 8,
    status: '',
    tickSizeEv: 0.00000001,
    code: 0,
    depositOpen: false,
    withdrawOpen: false,
    assetsPrecision: 8,
    perpetual: 0,
    stableCoin: 0,
    ...info,
  };
}

export function convertCurrencyInputConfig(currencyInfo: CurrencyEntity): {
  code: number;
  currency: string;
  displayCurrency: string;
  precision: number;
  scale: number;
  tickSizeEv: number;
  factor: number;
} {
  if (currencyInfo) {
    return {
      code: currencyInfo.code,
      currency: currencyInfo.currency,
      displayCurrency: currencyInfo.displayCurrency,
      precision: currencyInfo.valuePrecision,
      scale: 0,
      tickSizeEv: Math.pow(10, -currencyInfo.valuePrecision),
      factor: 1,
    };
  }

  return {code: 0, currency: '', displayCurrency: '', precision: 8, scale: 0, tickSizeEv: -1e8, factor: 1};
}
